package com.evoapps.evoshift.domain.util

import kotlin.concurrent.Volatile
import kotlin.time.Clock
import kotlin.time.Duration
import kotlin.time.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime

/**
 * The app's "now" (SH-144). Production code reads the time here, never from `Clock.System`
 * directly, so the tests can move it:
 *
 * - In the apps it is the system clock.
 * - JVM test runs with the system property `evoshift.fakeNow` (a local date-time such as
 *   `2027-03-01T00:05` in the default time zone; Gradle: `-PfakeNow=…`, docs/testing.md) start at
 *   that moment and the clock runs on from there, so a whole suite can run just after midnight on
 *   the 1st of a month or on a daylight-saving day.
 * - A test that needs one exact moment calls [pin] (and [unpin] afterwards).
 *
 * The time zone stays `TimeZone.currentSystemDefault()` (on the JVM: `-Duser.timezone`).
 */
object AppClock : Clock {
    private val offset: Duration = fakeNowProperty()?.let(::offsetTo) ?: Duration.ZERO

    @Volatile
    private var pinned: Instant? = null

    override fun now(): Instant = pinned ?: (Clock.System.now() + offset)

    /** Today's date in [zone] (by default the device's). */
    fun today(zone: TimeZone = TimeZone.currentSystemDefault()): LocalDate = now().toLocalDateTime(zone).date

    /** Tests only: "now" is [at] until [unpin]. */
    fun pin(at: Instant) {
        pinned = at
    }

    fun unpin() {
        pinned = null
    }

    /** True when a test run moved the clock (`evoshift.fakeNow`). */
    val isFaked: Boolean get() = offset != Duration.ZERO

    private fun offsetTo(localDateTime: String): Duration {
        val start = runCatching { LocalDateTime.parse(localDateTime.trim()) }.getOrElse {
            throw IllegalArgumentException("evoshift.fakeNow must be a local date-time like 2027-03-01T00:05, not '$localDateTime'", it)
        }
        return start.toInstant(TimeZone.currentSystemDefault()) - Clock.System.now()
    }
}

/** `evoshift.fakeNow` on the JVM (Android host tests, desktop); null on the other platforms and in the apps. */
internal expect fun fakeNowProperty(): String?
